import FormValidation from '../../../../../Seven.Neos.NodeTypes/Resources/Private/Vendor/formvalidation/es6/core/Core';
import Declarative from '../../../../../Seven.Neos.NodeTypes/Resources/Private/Vendor/formvalidation/es6/plugins/Declarative';
import Trigger from '../../../../../Seven.Neos.NodeTypes/Resources/Private/Vendor/formvalidation/es6/plugins/Trigger';
import AutoFocus from '../../../../../Seven.Neos.NodeTypes/Resources/Private/Vendor/formvalidation/es6/plugins/AutoFocus';
import Bootstrap5 from '../../../../../Seven.Neos.NodeTypes/Resources/Private/Vendor/formvalidation/es6/plugins/Bootstrap5';
import Icon from '../../../../../Seven.Neos.NodeTypes/Resources/Private/Vendor/formvalidation/es6/plugins/Icon';

Alpine.data('SevenCRMCheckout', (element) => ({
    product: {},
    loading: false,

    form: element.querySelector('form'),
    formValidation: null,

    applePay: {
        couponCode: '',
        supportsCouponCode: false,

        total: {
            // label: 'Product 123', // use this to override the product name (default is store name)
            amount: '5.00',
        },

        lineItems: [],
    },

    // @see https://braintree.github.io/braintree-web/3.34.0/PayPalCheckout.html#createPayment
    braintreePaypal: {
        amount: 0,
        lineItems: [],
        shippingAddressOverride: {},
    },

    paypal: {
        store: element.dataset.storeIdentifier,
        basket: [],
        attributes: {
            cancelUrl: element.dataset.cancelUrl,
            redirectUrl: element.dataset.redirectUrl,
        },
    },

    hostedFieldSets: {},
    paypalCardFields: {},
    mollieComponentSets: {},
    checkoutComFrames: {},

    payment: {
        method: 'paypal',
        token: { nonce: '', type: '', threeDSecureAuthenticationId: '' },
    },

    hooks: new Map(),

    init() {
        // product event listeners
        this._addEventListeners({
            'seven:checkout:product:selected': async (e) => {
                this.product = e.detail;

                const { lineItemName, totalPrice: amountInt } = e.detail;
                const amountFloat = amountInt / 100;

                this.braintreePaypal.amount = amountFloat;
                this.braintreePaypal.lineItems = [{
                    quantity: '1',
                    unitAmount: amountFloat.toFixed(2),
                    name: lineItemName,
                    kind: 'debit',
                }];

                this.paypal.basket = [e.detail.id];

                this.applePay.total.label = lineItemName;
                this.applePay.total.amount = String(amountFloat);
                this.applePay.lineItems = [{
                    label: lineItemName,
                    amount: String(amountFloat),
                    type: 'final',
                }];

                if (e.detail.source === 'manual') {
                    this.form.scrollIntoView({ behavior: 'smooth' });
                }
            },
        });

        this.form.setAttribute('novalidate', true); // use our own validation
        this.form.addEventListener('submit', this._onSubmit.bind(this));

        const hasPaymentError = document.location.search.toLowerCase().includes('paymenterror');

        if (hasPaymentError) {
            setTimeout(() => {
                (element.querySelector('.alert') ?? this.form).scrollIntoView({ behavior: 'smooth' });
            }, 250);
        }

        this._initFormValidation();
        this._initHostedFieldSets();
        this._initApplePay();

        this._initPayPal();
        this._initPayPalCardFields();

        this._initCheckoutComFrames();
        this._initMollieComponentSets();

        // Firefox does not reload the back on navigate back. Instead, we have to listen for pageshow.
        window.addEventListener('pageshow', (event) => {
            this.toggleLoader(false);
            Object.assign(this.payment.token, { type: '', nonce: '' });
        });
    },

    _initFormValidation() {
        this.formValidation = new FormValidation(this.form, {
            plugins: {
                declarative: new Declarative({ html5Input: true }),
                trigger: new Trigger(),
                autoFocus: new AutoFocus({
                    onPrefocus: (field) => {
                        setTimeout(() => {
                            field.firstElement.scrollIntoView({ behavior: 'smooth' });
                        }, 200);
                    },
                }),
                bootstrap: new Bootstrap5({ rowSelector: '.mb-3' }),
                icon: new Icon({ valid: 'fa fa-check', invalid: 'fa fa-times', validating: 'fa fa-refresh' }),
            },
        });

        requestAnimationFrame(() => {
            this._dispatchEvent('init', { instance: this });
            this._dispatchEvent('formvalidation:init', { instance: this.formValidation });
        });

        this.formValidation.on('core.form.valid', () => this.toggleLoader(false));
        this.formValidation.on('core.form.invalid', () => this.toggleLoader(false));
        this.formValidation.on('core.form.validating', () => {
            this.toggleLoader(true);
            this.hostedFieldSets[this.payment.method]?.validate();
            this.checkoutComFrames[this.payment.method]?.validate();
            this.mollieComponentSets[this.payment.method]?.validate();
        });

        document.addEventListener('seven:checkout:address:selected', (e) => {
            this._validate();
        });
    },

    /**
     * Hosted Fields
     * - Credit Card
     */
    _initHostedFieldSets() {
        element.addEventListener('seven:braintree:hostedfieldset:init', (e) => {
            this.hostedFieldSets[e.detail.name] = e.detail;
            e.detail.instance.on('inputSubmitRequest', () => this.requestSubmit(true));
        });
    },

    _initPayPalCardFields() {
        element.addEventListener('seven:payment:paypal:cardfields:init', (e) => {
            this.paypalCardFields[e.detail.id] = e.detail;
        });

        element.addEventListener('seven:payment:paypal:cardfields:inputsubmitrequest', (e) => {
            this.requestSubmit(true);
        });
    },

    /**
     * Mollie Components
     * - Credit Card
     */
    _initMollieComponentSets() {
        element.addEventListener('seven:mollie:componentset:init', (e) => {
            this.mollieComponentSets[e.detail.id] = e.detail;
        });
    },

    /**
     * Checkout.com Frames
     * - Credit Card
     */
    _initCheckoutComFrames() {
        element.addEventListener('seven:checkoutcom:frames:init', (e) => {
            this.checkoutComFrames[e.detail.id] = e.detail;
        });
    },

    /**
     * Apple Pay
     */
    _initApplePay() {
        this._addEventListeners({
            'seven:braintree:applepay:sessioncreated': (e) => {
                const { id } = e.detail;
                this.payment.method = id;
            },
            'seven:braintree:applepay:paymentauthorized': (e) => {
                const { paymentToken, id } = e.detail;
                Object.assign(this.payment.token, paymentToken);
                this.payment.method = id;
                this.requestSubmit(true);
            },
            'seven:braintree:applepay:shippingcontactselected': e => {
                // @todo: provide a list of supported shipping country codes via fusion
                if (e.detail.shippingContact.countryCode !== 'US') {
                    e.preventDefault();
                    e.stopPropagation();

                    e.detail.session.completeShippingContactSelection({
                        newTotal: e.detail.paymentRequest.total,
                        errors: [
                            // https://developer.apple.com/documentation/apple_pay_on_the_web/applepayerrorcode
                            // https://developer.apple.com/documentation/apple_pay_on_the_web/applepayerrorcontactfield
                            new ApplePayError('shippingContactInvalid', 'country'),
                        ],
                    });
                }
            },
        });
    },

    /**
     * PayPal
     */
    _initPayPal() {
        this._addEventListeners({
            // braintree
            'seven:braintree:paypal:buttonclick': async e => {
                const isValid = await this._validate();
                e.detail.actions[isValid ? 'resolve' : 'reject']();
            },
            'seven:braintree:paypal:paymentauthorized': e => {
                const { paymentToken } = e.detail;
                Object.assign(this.payment.token, paymentToken);
                this.requestSubmit(true);
            },
            'seven:braintree:paypal:paymentcreated': () => this.toggleLoader(false),
            'seven:braintree:paypal:cancel': () => this.toggleLoader(false),
            'seven:braintree:paypal:error': () => this.toggleLoader(false),

            // paypal
            'seven:payment:paypal:button:click': async e => {
                const idValid = await this._validate();
                e.detail.actions[idValid ? 'resolve' : 'reject']();
            },
            'seven:payment:paypal:button:cancel': () => this.toggleLoader(false),
            'seven:payment:paypal:button:error': () => this.toggleLoader(false),
            'seven:payment:paypal:button:paymentcompleted': () => this.toggleLoader(true),

            'seven:payment:paypal:applepay:sessioncreated': async (e) => {
                e.preventDefault();
                this.toggleLoader(true);
                const isValid = await this._validate();
                if (isValid) e.detail.session.begin();
                this.toggleLoader(false);
            },
            'seven:payment:paypal:applepay:paymentcompleted': () => this.toggleLoader(true),
            'seven:payment:paypal:cardfields:inputsubmitrequest': () => this.requestSubmit(true),
        });
    },

    /////////////////////////
    // Helpers
    /////////////////////////
    _addEventListeners(listeners) {
        Object.entries(listeners).forEach((args) => element.addEventListener(...args));
    },

    async _onSubmit(e) {
        e?.preventDefault();
        e?.stopPropagation();

        this.toggleLoader(true);
        if (!await this._validate()) {
            this.toggleLoader(false);
            return;
        }

        const hostedFieldSet = this.hostedFieldSets[this.payment.method];
        const mollieComponentSet = this.mollieComponentSets[this.payment.method];
        const checkoutComFrame = this.checkoutComFrames[this.payment.method];
        const paypalCardField = this.paypalCardFields[this.payment.method];

        if (hostedFieldSet && (!this.payment.token.nonce || hostedFieldSet.tokenType !== this.payment.token.type)) {
            this.toggleLoader(true);

            try {
                const { paymentToken, threeDSecure } = await hostedFieldSet.tokenize();

                if (threeDSecure) {
                    const verifyRes = await threeDSecure.verifyCard({
                        amount: this.product.totalPrice / 100,
                        nonce: paymentToken.nonce,
                        bin: paymentToken.details.bin,
                    });

                    if (!verifyRes) {
                        // event was canceled
                        this.toggleLoader(false);
                        return;
                    }

                    if (verifyRes.liabilityShiftPossible) {
                        if (!verifyRes.liabilityShifted) {
                            // 3D Secure authentication failed
                            this.toggleLoader(false);
                            return;
                        }
                    }

                    // Scenario 1)
                    // 3D Secure worked and authentication succeeded
                    // This will also be true if the issuing bank does not support 3D Secure, but the payment method does.
                    // In both cases, the liability for fraud has been shifted to the bank.
                    //
                    // Scenario 2)
                    // The card was ineligible for 3D Secure, but we can still process it as a non-3D Secure transaction.
                    Object.assign(this.payment.token, {
                        type: paymentToken.type,
                        nonce: verifyRes.nonce,
                        threeDSecureAuthenticationId: verifyRes.threeDSecureInfo?.threeDSecureAuthenticationId,
                    });
                } else if (paymentToken) {
                    Object.assign(this.payment.token, paymentToken);
                } else {
                    this.toggleLoader(false);
                }

                if (this.payment.token) {
                    await this.requestSubmit(true);
                }
            } catch (error) {
                console.error(error);
                this.toggleLoader(false);
            }

            return;
        }

        if (mollieComponentSet && (!this.payment.token.nonce || mollieComponentSet.tokenType !== this.payment.token.type)) {
            setTimeout(() => this.toggleLoader(true), 50);
            const { paymentToken } = await mollieComponentSet.tokenize();

            if (paymentToken.nonce) {
                Object.assign(this.payment.token, {
                    type: paymentToken.type,
                    nonce: paymentToken.nonce,
                });

                await this.requestSubmit(true);
                return;
            }

            this.toggleLoader(false);
            setTimeout(() => this.toggleLoader(false), 50);
            return;
        }

        if (checkoutComFrame && (!this.payment.token.nonce || checkoutComFrame.tokenType !== this.payment.token.type)) {
            setTimeout(() => this.toggleLoader(true), 50);
            const { paymentToken } = await checkoutComFrame.tokenize();

            if (paymentToken.nonce) {
                Object.assign(this.payment.token, {
                    type: paymentToken.type,
                    nonce: paymentToken.nonce,
                });

                await this.requestSubmit(true);
                return;
            }

            this.toggleLoader(false);
            setTimeout(() => this.toggleLoader(false), 50);
            return;
        }

        if (paypalCardField && (!this.payment.token.nonce || paypalCardField.tokenType !== this.payment.token.type)) {
            try {
                this.toggleLoader(true);
                await paypalCardField.submit();
            } catch (error) {
                this.toggleLoader(false);
            }

            return;
        }

        this.toggleLoader(true);
        this.form.submit();
    },

    async _validate() {
        const res = await this.formValidation.validate();
        return res.toLowerCase() === 'valid';
    },

    async requestSubmit(force = false) {
        this.toggleLoader(true);
        const isValid = await this._validate();

        if (!isValid) {
            await this.toggleLoader(false);
            return;
        }

        requestAnimationFrame(() => { // jump into next tick to ensure the value is set
            const button = this.form.querySelector('button[type="submit"]:not([disabled])');
            this.toggleLoader(true);

            if (force || button) {
                if (this.form.requestSubmit) {
                    this.form.requestSubmit();
                } else {
                    button.click();
                }
            } else {
                this.toggleLoader(false);
            }
        });
    },

    toggleLoader(show) {
        this.loading = show;
    },

    _dispatchEvent(name, { cancelable, bubbles, ...detail } = {}) {
        return element.dispatchEvent(new CustomEvent(`seven:crm:checkout:${name}`, {
            detail,
            bubbles: bubbles ?? true,
            cancelable: cancelable ?? true,
        }));
    },
}));
